import React, { useMemo, useState } from 'react'
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    Box,
    Typography,
    IconButton,
    Link,
    Divider,
    Grid,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import AttachFileIcon from '@mui/icons-material/AttachFile'
import { useSubmitReview, useGetTask, useGetToReviews } from '../../../services/api/task-management/trackerHooks'
import { RATING_ICONS } from '../helper/ratingIcons'

const ReviewSubmissionModal = ({
    open,
    onClose,
    reviewData,
    onSubmit,
    selectedTask,
    setSuccessMessage,
    setErrorMessage,
}) => {

    // ---- Logged-in user (Submit by) ----
    // const { data: myAccountData, isLoading: isMyAccountLoading } = useMyAccount()
    // const submitByName = myAccountData?.data?.staff?.name || 'NA'

    // ---- Local state ----
    const [rating, setRating] = useState(null) // 'like', 'neutral', 'dislike'
    const [feedback, setFeedback] = useState('')
    const [hovered, setHovered] = useState(null)
    const [validationError, setValidationError] = useState('')

    // ---- Submit review mutation ----
    const { mutate: submitReview, isLoading: isSubmitting } = useSubmitReview()

    // ---- Derive taskId once (used by useGetTask + submit) ----
    const taskId = useMemo(() => {
        const id =
            reviewData?.id ||
            reviewData?.selectedTask?.id ||
            reviewData?.tasks?.[0]?.id

        console.log('Derived taskId:', id)
        return id
    }, [selectedTask, reviewData])

    // ---- Get task detail (to display task.value) ----
    const { data: taskRes, isLoading: isTaskLoading } = useGetTask(taskId)

    // Value from API: data.task.value (e.g. "5.00")
    const taskValueDisplay = useMemo(() => {
        const raw = taskRes?.data?.task?.value
        if (raw == null || raw === '') return 'NA'
        const num = Number(raw)
        return Number.isFinite(num) ? num.toFixed() : String(raw)
    }, [taskRes])

    const { data: toReviewsData, isLoading: isToReviewsLoading } = useGetToReviews()

    const submitByName = useMemo(() => {
        const list = toReviewsData?.data?.to_review || []   // ✅ FIXED PATH
        if (!taskId) return 'NA'

        const item = list.find((t) => String(t?.id) === String(taskId))
        if (!item) return 'NA'

        const submissions = item?.submissions || []
        if (!submissions.length) return 'NA'

        // get latest submission safely
        const latest = [...submissions].sort((a, b) => {
            const an = Number(a?.submission_number ?? 0)
            const bn = Number(b?.submission_number ?? 0)
            return bn - an
        })[0]

        return latest?.submitted_by?.name || 'NA'
    }, [toReviewsData, taskId])

    // ---- Latest submission (compat: new backend vs old fields) ----
    // const latestSubmission = useMemo(() => {

    //     return (
    //         reviewData?.selectedTask?.rawData?.rawData?.submissions ||
    //         reviewData?.selectedTask?.rawData?.submissions ||
    //         reviewData?.rawData?.submissions ||
    //         reviewData?.submissions || {
    //             submission_remarks: reviewData?.selectedTask?.rawData?.remarks || 'No remarks provided.',
    //             submission_link: reviewData?.selectedTask?.rawData?.links?.url || 'No link provided.',
    //             attachments: reviewData?.selectedTask?.rawData?.attachments || [],
    //         }
    //     );
    // }, [reviewData])
    const latestSubmission = useMemo(() => {
        // Get the submissions array from whichever path has it
        const submissionsArray =
            reviewData?.selectedTask?.rawData?.rawData?.submissions ||
            reviewData?.selectedTask?.rawData?.submissions ||
            reviewData?.rawData?.submissions ||
            reviewData?.submissions

        // If it's an array, sort and pick the latest submission
        if (Array.isArray(submissionsArray) && submissionsArray.length > 0) {
            const sorted = [...submissionsArray].sort((a, b) => {
                const an = Number(a?.submission_number ?? 0)
                const bn = Number(b?.submission_number ?? 0)
                return bn - an // descending → latest first
            })
            return sorted[0] // always returns latest submission object
        }

        // If it's already a single object (not an array), use it directly
        if (submissionsArray && !Array.isArray(submissionsArray)) {
            return submissionsArray
        }

        // Fallback for old data structure
        return {
            submission_remarks: reviewData?.selectedTask?.rawData?.remarks || 'No remarks provided.',
            submission_link: reviewData?.selectedTask?.rawData?.links?.url || 'No link provided.',
            attachments: reviewData?.selectedTask?.rawData?.attachments || [],
        }
    }, [reviewData])

    // Simplified URL normalizer - backend now returns presigned URLs
    const normalizeUrl = (raw) => {
        if (!raw) return ''
        const url = String(raw).trim()

        // If it's already a full URL (including presigned URLs), use as-is
        if (/^https?:\/\//i.test(url)) return url

        // Fallback: if missing protocol, assume https
        return `https://${url}`
    }

    // ---- Task name fallback ----
    const displayTaskName =
        selectedTask?.name ||           // ← This will now work!
        reviewData?.taskDetails?.name ||
        reviewData?.task?.name ||
        reviewData?.taskName ||
        ''

    // Rating mapping: frontend → backend
    const ratingMap = {
        like: 'two_thumbs_up',
        neutral: 'thumbs_up',
        dislike: 'thumbs_down',
    }

    const handleSubmit = () => {
        setValidationError('')

        if (!rating) {
            setValidationError('Please select a rating')
            return
        }

        // if (!feedback.trim()) {
        //     setValidationError('Please enter your feedback')
        //     return
        // }

        if (!taskId) {
            setValidationError('Task ID not found. Please refresh and try again.')
            return
        }

        const backendRating = ratingMap[rating]

        const reviewPayload = {
            rating: backendRating,
            feedback: feedback.trim() || null,
        }

        submitReview(
            {
                taskId,
                reviewData: reviewPayload,
            },
            {
                onSuccess: (data) => {
                    console.log('Review submitted:', data)

                    if (setSuccessMessage) {
                        setSuccessMessage('Review submitted successfully!')
                        setTimeout(() => setSuccessMessage(''), 5000)
                    }

                    if (onSubmit) {
                        onSubmit({
                            ...reviewData,
                            rating,
                            feedback,
                            reviewedDate: new Date().toISOString(),
                            submittedBy: submitByName,
                        })
                    }

                    handleClose()
                },
                onError: (error) => {
                    console.error('Error submitting review:', error)
                    const errorMsg =
                        error?.response?.data?.message || 'Failed to submit task. Please try again.'
                    if (setErrorMessage) {
                        setErrorMessage(errorMsg)
                        setTimeout(() => setErrorMessage(''), 5000)
                    }
                },
            }
        )
    }

    const handleClose = () => {
        setRating(null)
        setFeedback('')
        setValidationError('')
        onClose()
    }

    if (!reviewData) return null

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 2,
                    // maxWidth: '500px',
                },
            }}
        >
            <DialogTitle sx={{ pb: 2 }}>
                <Box display="flex" justifyContent="space-between" alignItems="center">
                    <Box display="flex" alignItems="center" gap={1}>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                width: 45,
                                height: 45,
                                borderRadius: '50%',
                                backgroundColor: '#DEECFF',
                                p: 0.5,
                            }}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="19" height="21" viewBox="0 0 19 21" fill="none">
                                <path
                                    d="M13 16C13.56 16 14 16.44 14 17C14 17.56 13.56 18 13 18C12.44 18 12 17.56 12 17C12 16.44 12.44 16 13 16ZM13 13C10.27 13 7.94 14.66 7 17C7.94 19.34 10.27 21 13 21C15.73 21 18.06 19.34 19 17C18.06 14.66 15.73 13 13 13ZM13 19.5C12.337 19.5 11.7011 19.2366 11.2322 18.7678C10.7634 18.2989 10.5 17.663 10.5 17C10.5 16.337 10.7634 15.7011 11.2322 15.2322C11.7011 14.7634 12.337 14.5 13 14.5C13.663 14.5 14.2989 14.7634 14.7678 15.2322C15.2366 15.7011 15.5 16.337 15.5 17C15.5 17.663 15.2366 18.2989 14.7678 18.7678C14.2989 19.2366 13.663 19.5 13 19.5ZM5.27 18H2V2H9V7H14V11.07C14.7 11.15 15.36 11.32 16 11.56V6L10 0H2C1.46957 0 0.960859 0.210714 0.585786 0.585786C0.210714 0.960859 0 1.46957 0 2V18C0 18.5304 0.210714 19.0391 0.585786 19.4142C0.960859 19.7893 1.46957 20 2 20H6.5C5.99618 19.3958 5.58194 18.7222 5.27 18Z"
                                    fill="#2666BE"
                                />
                            </svg>
                        </Box>

                        <Typography fontSize={20} fontWeight={500}>
                            Review Submission
                        </Typography>
                    </Box>

                    <IconButton onClick={handleClose} size="small">
                        <CloseIcon />
                    </IconButton>
                </Box>
            </DialogTitle>

            <Divider />

            <DialogContent sx={{ pt: 2, overflowX: 'hidden' }}>
                {validationError && (
                    <Box
                        mb={2}
                        sx={{
                            backgroundColor: '#FFF0F0',
                            border: '1px solid #F5C6CB',
                            borderRadius: 1,
                            p: 1.5,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 1,
                        }}
                    >
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" style={{ flexShrink: 0 }}>
                            <circle cx="12" cy="12" r="10" stroke="#C62828" strokeWidth="2" />
                            <path d="M12 7V12" stroke="#C62828" strokeWidth="2" strokeLinecap="round" />
                            <circle cx="12" cy="16" r="1" fill="#C62828" />
                        </svg>
                        <Typography sx={{ fontSize: 14, fontWeight: 500, color: '#7A1F1F' }}>
                            {validationError}
                        </Typography>
                    </Box>
                )}

                <Box display="flex" flexDirection="column">
                    {/* Task */}
                    <Grid container spacing={2} mb="10px" alignItems="center" justifyContent="flex-start">
                        <Grid item xs={3}>
                            <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
                                Task:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography fontSize="16px" color="#4a4a4a" fontWeight={400}>
                                {displayTaskName}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Project */}
                    <Grid container spacing={2} mb="10px" alignItems="center" justifyContent="flex-start">
                        <Grid item xs={3}>
                            <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
                                Project:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography fontSize="16px" color="#4a4a4a" fontWeight={400}>
                                {reviewData.projectName}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Submit by (login user) */}
                    <Grid container spacing={2} mb="10px" alignItems="center" justifyContent="flex-start">
                        <Grid item xs={3}>
                            <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
                                Submit by:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography fontSize="16px" color="#4a4a4a" fontWeight={400}>
                                {isToReviewsLoading ? 'Loading…' : submitByName}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Value (from getTask) */}
                    <Grid container spacing={2} mb="10px" alignItems="center" justifyContent="flex-start">
                        <Grid item xs={3}>
                            <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
                                Value:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            <Typography fontSize="16px" color="#4a4a4a" fontWeight={400}>
                                {isTaskLoading ? 'Loading…' : taskValueDisplay}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Remarks */}
                    <Grid container spacing={2} mb="10px" alignItems="start" justifyContent="flex-start">
                        <Grid item xs={3}>
                            <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
                                Remarks:
                            </Typography>
                        </Grid>
                        <Grid item xs={9}>
                            <Typography fontSize="16px" color="#4a4a4a" fontWeight={400} sx={{ whiteSpace: 'pre-wrap'}}>
                                {latestSubmission?.submission_remarks || 'No remarks provided.'}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Links */}
                    <Grid container spacing={2} mb="10px" alignItems="center" justifyContent="flex-start">
                        <Grid item xs={3}>
                            <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
                                Links:
                            </Typography>
                        </Grid>
                        <Grid item xs={6} sx={{ minWidth: 0, width: '100%' }}>
                            <Typography fontSize="16px" color="#4a4a4a" fontWeight={400}>
                                {latestSubmission?.submission_link ? (
                                    <Link
                                        href={latestSubmission.submission_link}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            display: 'block',
                                            maxWidth: '100%',
                                            color: '#2F80ED',
                                            textDecoration: 'none',
                                            fontSize: '14px',
                                            mb: 0.5,
                                            whiteSpace: 'normal',
                                            overflowWrap: 'anywhere',
                                            wordBreak: 'break-word',
                                        }}
                                    >
                                        {latestSubmission.submission_link}
                                    </Link>
                                ) : reviewData.links && reviewData.links.length > 0 ? (
                                    reviewData.links.map((link, index) => (
                                        <Link
                                            key={index}
                                            href={link.url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 0.5,
                                                color: '#2F80ED',
                                                textDecoration: 'none',
                                                fontSize: '14px',
                                                mb: 0.5,
                                                whiteSpace: 'normal',
                                                overflowWrap: 'anywhere',
                                                wordBreak: 'break-word',
                                            }}
                                        >
                                            {link.text || link.url}
                                        </Link>
                                    ))
                                ) : (
                                    <Typography fontSize="16px" color="#4a4a4a">
                                        No links provided.
                                    </Typography>
                                )}
                            </Typography>
                        </Grid>
                    </Grid>

                    {/* Attachments */}
                    <Grid container spacing={2} mb="10px" alignItems="center" justifyContent="flex-start">
                        <Grid item xs={3}>
                            <Typography fontSize="14px" color="text.secondary" fontWeight={500}>
                                Attachments:
                            </Typography>
                        </Grid>
                        <Grid item xs={6}>
                            {latestSubmission?.attachments && latestSubmission.attachments.length > 0 ? (
                                latestSubmission.attachments.map((file, idx) => {
                                    let fileUrl = ''
                                    let fileName = `Attachment ${idx + 1}`

                                    if (typeof file === 'string') {
                                        fileUrl = normalizeUrl(file)
                                        fileName = file.split('/').pop() || fileName
                                    } else {
                                        const possible =
                                            file.url ||
                                            file.file_url ||
                                            file.path ||
                                            file.file_path ||
                                            file.key ||
                                            file.location
                                        fileUrl = normalizeUrl(possible)
                                        fileName =
                                            file.name ||
                                            file.file_name ||
                                            (fileUrl ? fileUrl.split('/').pop() : fileName)
                                    }

                                    const href = fileUrl && fileUrl !== '' ? fileUrl : '#'

                                    return (
                                        <Link
                                            key={idx}
                                            href={href}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            sx={{ display: 'block', color: '#2F80ED' }}
                                        >
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <AttachFileIcon sx={{ fontSize: 16 }} />
                                                {fileName}
                                            </Box>
                                        </Link>
                                    )
                                })
                            ) : (
                                <Typography fontSize="16px" color="#4a4a4a" fontWeight={400}>
                                    {reviewData.attachments && reviewData.attachments.length > 0
                                        ? reviewData.attachments.join(', ')
                                        : 'No attachment(s) provided.'}
                                </Typography>
                            )}
                        </Grid>
                    </Grid>

                    <Divider />

                    {/* Rating */}
                    <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', mb: 1 }}>
                        <Typography fontSize="16px" color="text.primary" fontWeight={500} mb={2}>
                            Your Review:
                        </Typography>
                        <Typography fontSize="14px" color="#3a3a3a" display="block" mb={1}>
                            Ratings
                        </Typography>

                        <Box display="flex" gap={2} mb={1}>
                            {/* Like */}
                            <IconButton
                                onClick={() => setRating('like')}
                                sx={{
                                    border: '1px solid #f3bf3a',
                                    // backgroundColor:
                                    //     rating === 'like' ? '#FFC72C' : hovered === 'like' ? '#E5A900' : '#FFF9E6',
                                    backgroundColor: rating === 'like'
                                        ? '#FFC72C'
                                        : (hovered === 'like' && rating === null)
                                            ? '#E5A900'
                                            : '#FFF9E6',
                                    '&:hover': {
                                        backgroundColor: rating === 'like' ? '#FFC72C' : '#E5A900',
                                    },
                                    borderRadius: '5px',
                                    width: 48,
                                    height: 48,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background 0.2s',
                                    boxShadow: rating === 'like' ? '0 0 0 1px #f3bf3a' : 'none',
                                }}
                                onMouseEnter={() => setHovered('like')}
                                onMouseLeave={() => setHovered(null)}
                            >
                                {RATING_ICONS[ratingMap['like']]}
                            </IconButton>

                            {/* Neutral */}
                            <IconButton
                                onClick={() => setRating('neutral')}
                                sx={{
                                    border: '1px solid #f3bf3a',
                                    backgroundColor: rating === 'neutral'
                                        ? '#FFC72C'
                                        : (hovered === 'neutral' && rating === null)
                                            ? '#E5A900'
                                            : '#FFF9E6',
                                    '&:hover': {
                                        backgroundColor: rating === 'neutral' ? '#FFC72C' : '#E5A900',
                                    },
                                    borderRadius: '5px',
                                    width: 48,
                                    height: 48,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background 0.2s',
                                    boxShadow: rating === 'neutral' ? '0 0 0 1px #f3bf3a' : 'none',
                                }}
                                onMouseEnter={() => setHovered('neutral')}
                                onMouseLeave={() => setHovered(null)}
                            >
                                {RATING_ICONS[ratingMap['neutral']]}
                            </IconButton>

                            {/* Dislike */}
                            <IconButton
                                onClick={() => setRating('dislike')}
                                sx={{
                                    // border: '1px solid #f3bf3a',
                                    border: rating === 'dislike' ? '1px solid #eb5757' : '1px solid #f3bf3a',
                                    // backgroundColor: rating === 'dislike' ? '#eb5757' : (hovered === 'dislike' ? '#eb5757' : '#fdf6e2'),
                                    backgroundColor: rating === 'dislike'
                                        ? '#eb5757'
                                        : (hovered === 'dislike' && rating === null)
                                            ? '#eb5757'
                                            : '#fdf6e2',
                                    '&:hover': {
                                        backgroundColor: rating === 'dislike' ? '#eb5757' : '#eb5757',
                                    },
                                    borderRadius: '5px',
                                    width: 48,
                                    height: 48,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    transition: 'background 0.2s',
                                    boxShadow: rating === 'dislike' ? '0 0 0 1px #eb5757' : 'none',
                                }}
                                onMouseEnter={() => setHovered('dislike')}
                                onMouseLeave={() => setHovered(null)}
                            >
                                {RATING_ICONS[ratingMap['dislike']]}
                            </IconButton>
                        </Box>
                    </Box>

                    {/* Feedback */}
                    <Box>
                        <Typography fontSize="14px" color="#3a3a3a" display="block" mb={1}>
                            Feedback (Optional)
                            {/* <span style={{ color: '#D32F2F' }}>*</span> */}
                        </Typography>
                        <TextField
                            fullWidth
                            multiline
                            rows={10}
                            value={feedback}
                            onChange={(e) => {
                                const words = e.target.value.trim() === '' ? [] : e.target.value.trim().split(/\s+/)
                                if (words.length <= 500) {
                                    setFeedback(e.target.value)
                                }
                            }}
                            placeholder="Enter your feedback here..."
                            sx={{
                                outline: 'none !important',
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 1.5,
                                    outline: 'none !important',
                                    '& fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important',
                                    },
                                    '&:hover fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        outline: 'none !important',
                                    },
                                    '&.Mui-focused fieldset': {
                                        borderColor: '#8A8A8A !important',
                                        borderWidth: '1px !important',
                                        outline: 'none !important',
                                    },
                                    '&.Mui-focused': {
                                        boxShadow: 'none !important',
                                        outline: 'none !important',
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important',
                                    },
                                },
                                '& textarea': {
                                    outline: 'none !important',
                                    '&:focus': {
                                        outline: 'none !important',
                                        boxShadow: 'none !important',
                                    },
                                    '&:focus-visible': {
                                        outline: 'none !important',
                                    },
                                },
                                '&:focus-within': {
                                    outline: 'none !important',
                                },
                            }}
                        />
                        <Typography variant="caption" color="text.secondary" sx={{ float: 'right', mt: 0.5 }}>
                            {feedback.trim() === '' ? 0 : feedback.trim().split(/\s+/).length}/500
                        </Typography>
                    </Box>
                </Box>
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, gap: 1, backgroundColor: '#FFF9E6' }}>
                <Button
                    onClick={handleClose}
                    variant="outlined"
                    disabled={isSubmitting}
                    sx={{
                        textTransform: 'none',
                        borderColor: '#f3bf3a',
                        maxWidth: '150px',
                        color: 'text.primary',
                        borderRadius: '100px',
                        '&:hover': {
                            borderColor: '#f3bf3a',
                            bgcolor: '#Fdf6e2',
                        },
                    }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }}>Cancel</Typography>
                </Button>

                <Button
                    onClick={handleSubmit}
                    variant="contained"
                    // disabled={!rating || !feedback.trim() || isSubmitting}
                    sx={{
                        textTransform: 'none',
                        borderRadius: '100px',
                        maxWidth: '150px',
                        boxShadow: 'none',
                        backgroundColor: '#F3bf3a',
                        '&:hover': {
                            backgroundColor: '#d09a01',
                            boxShadow: 'none',
                        },
                    }}
                >
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: 'text.primary' }}>
                        {isSubmitting ? 'Submitting...' : 'Submit'}
                    </Typography>
                </Button>
            </DialogActions>
        </Dialog>
    )
}

export default ReviewSubmissionModal
